// ============================================
// FILE: src/components/modals/RateOrderModal.jsx
// ============================================
//
// "Rate Order" from the orders list.
//
// An order can hold several products and reviews are per-product, so this is a
// picker rather than a form: choose which item to rate, then the normal review
// modal opens for it. Eligibility is fetched per item so a product that has
// already been reviewed shows its rating instead of an empty CTA.

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, Check } from 'lucide-react';
import StarRating from '../fashion/StarRating';
import WriteReviewModal from '../fashion/WriteReviewModal';
import { resolveProductImageUrl } from '../../utils/imageHelpers';
import orderHelpers from '../../utils/orderHelpers';
import apiService from '../../apiService';

const RateOrderModal = ({
  isOpen,
  order,
  onClose,
  showToast,
  isDesktop = false,
}) => {
  const [eligibility, setEligibility] = useState({});   // productId -> payload
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState(null);         // the product being reviewed

  // Reviews are per product, so collapse duplicate lines (same product bought
  // in two sizes is still one thing to review).
  const items = orderHelpers.normalizeOrderItems(order || {})
    .filter((i) => i.productId)
    .reduce((acc, item) => {
      if (!acc.some((existing) => String(existing.productId) === String(item.productId))) {
        acc.push(item);
      }
      return acc;
    }, []);

  const loadEligibility = useCallback(async () => {
    if (items.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const results = await Promise.all(
      items.map((i) => apiService.getReviewEligibility(i.productId))
    );

    const map = {};
    items.forEach((item, index) => {
      map[String(item.productId)] = results[index]?.data || null;
    });

    setEligibility(map);
    setLoading(false);
    // items is derived from `order` each render; keying on the order is what
    // actually matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  useEffect(() => {
    if (isOpen) loadEligibility();
  }, [isOpen, loadEligibility]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen) return null;

  // WriteReviewModal expects a product-shaped object; order items carry the
  // snapshot fields instead.
  const asProduct = (item) => ({
    id: item.productId,
    name: item.name,
    brand: item.brand || item.product?.brand || null,
    featured_image_url: resolveProductImageUrl(
      item.product_image || item.image || item.product?.featured_image_url
    ),
  });

  return (
    <>
      <div className="fashion-scope fixed inset-0 z-[320] flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />

        <div
          className="relative bg-white w-full overflow-y-auto"
          style={{
            maxWidth: isDesktop ? 520 : '100%',
            maxHeight: '85vh',
            borderRadius: isDesktop ? 4 : '12px 12px 0 0',
          }}
        >
          <div
            className="sticky top-0 bg-white flex items-center justify-between px-5 py-3"
            style={{ borderBottom: '1px solid var(--fx-line)' }}
          >
            <div>
              <span className="fx-label">Rate your order</span>
              <p style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
                #{order?.order_number || order?.id}
              </p>
            </div>
            <button onClick={onClose} className="p-1 -mr-1" aria-label="Close">
              <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
            </button>
          </div>

          <div className="px-5 py-4">
            {loading && (
              <div className="space-y-3">
                {[0, 1].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="bg-[#f5f5f6] animate-pulse" style={{ width: 56, height: 72, borderRadius: 2 }} />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-[#f5f5f6] w-2/3 animate-pulse" />
                      <div className="h-3 bg-[#f5f5f6] w-1/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && items.length === 0 && (
              <p className="py-6 text-center" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
                There is nothing to rate on this order.
              </p>
            )}

            {!loading && items.map((item) => {
              const state = eligibility[String(item.productId)];
              const mine = state?.my_review || null;
              const canReview = state?.can_review;
              const image = resolveProductImageUrl(
                item.product_image || item.image || item.product?.featured_image_url
              );

              return (
                <button
                  key={item.productId}
                  type="button"
                  onClick={() => setWriting(item)}
                  disabled={!canReview && !mine}
                  className="w-full flex items-center gap-3 py-3 text-left"
                  style={{
                    borderBottom: '1px solid var(--fx-line)',
                    opacity: (!canReview && !mine) ? 0.55 : 1,
                    cursor: (!canReview && !mine) ? 'default' : 'pointer',
                  }}
                >
                  <div
                    className="flex-shrink-0 bg-[#f5f5f6] overflow-hidden"
                    style={{ width: 56, height: 72, borderRadius: 2 }}
                  >
                    {image && <img src={image} alt="" className="w-full h-full object-cover" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{item.name}</p>
                    {item.variant_name && (
                      <p style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>{item.variant_name}</p>
                    )}

                    {mine ? (
                      <span className="inline-flex items-center gap-1.5 mt-1.5">
                        <StarRating value={mine.rating} size={12} />
                        <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: 'var(--fx-green)' }}>
                          <Check size={12} />
                          Rated
                        </span>
                      </span>
                    ) : (
                      <p className="mt-1.5" style={{ fontSize: 12, color: canReview ? 'var(--fx-pink)' : 'var(--fx-ink-3)', fontWeight: 700 }}>
                        {canReview ? 'RATE THIS PRODUCT' : 'Not available to rate'}
                      </p>
                    )}
                  </div>

                  {(canReview || mine) && (
                    <ChevronRight size={18} style={{ color: 'var(--fx-ink-3)', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {writing && (
        <WriteReviewModal
          isOpen
          onClose={() => setWriting(null)}
          product={asProduct(writing)}
          existingReview={eligibility[String(writing.productId)]?.my_review || null}
          onSaved={() => { setWriting(null); loadEligibility(); }}
          showToast={showToast}
          isDesktop={isDesktop}
        />
      )}
    </>
  );
};

export default RateOrderModal;
